import Header, { activeSection } from '../html/parts/Header/Header.js';
import './includes.js';
import { initCartUi } from './bookingDraft.js';
import { resetScrollOnRefresh } from './ScrollAfterRefresh.js';
import displayEuroSymbol from './displayEuroSymbol.js';
import cookieYesCompact from './cookieYesCompact.js';

// Jak najwcześniej: reload → góra + bez hash scroll (zanim OfferSubNav / native hash).
resetScrollOnRefresh();
// EUR → € (tylko wyświetlanie; wcześniej Custom CSS/JS w Theme Options).
displayEuroSymbol();
// CookieYes: kompaktowy baner + mobile FAB (nie zasłania sticky) — cookieyes.css sync.
cookieYesCompact();

// Uwaga: nie odczytywać clientWidth/offset* przy starcie — wymusza pełny layout
// (PageSpeed: ~400 ms forced reflow). Dawne --app-height / --scrollbar-width nie były
// używane w żadnym SCSS; usunięte.

const has = (selector) => Boolean(document.querySelector(selector));

/**
 * Ładuje moduł dopiero gdy pierwszy pasujący element zbliża się do viewportu
 * (sekcje daleko pod foldem nie konkurują o łącze/CPU z hero i app.css).
 * Bez IntersectionObserver / gdy element już blisko — ładuje od razu.
 *
 * Po podmianie DOM (np. js-term-content na Pingwinie) obserwowany węzeł może
 * zniknąć — wtedy ponownie bindujemy do aktualnego trafienia selektora.
 */
const whenNear = (selector, load, rootMargin = '400px 0px') => {
  let done = false;
  let io = null;
  let mo = null;

  const finish = () => {
    if (done) return;
    done = true;
    io?.disconnect();
    mo?.disconnect();
    load();
  };

  const current = () => document.querySelector(selector);

  if (!current()) return;

  if (typeof IntersectionObserver === 'undefined') {
    finish();
    return;
  }

  io = new IntersectionObserver((entries) => {
    if (done) return;
    if (!entries.some((e) => e.isIntersecting)) return;
    if (!current()) return;
    finish();
  }, { rootMargin });

  const observe = () => {
    const el = current();
    if (!el || done) return;
    try {
      io.observe(el);
    } catch {
      /* ignore */
    }
  };

  observe();

  // Swap treści (paczki kursów) podmienia gallery-js — dograj observer do nowego węzła.
  mo = new MutationObserver(() => {
    if (done) {
      mo.disconnect();
      return;
    }
    observe();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
};

let appBooted = false;

const runApp = async () => {
  // Guard na document — osobne instancje modułu app.js mają własne appBooted.
  if (appBooted || document.documentElement.dataset.hskiAppBooted === '1') return;
  appBooted = true;
  document.documentElement.dataset.hskiAppBooted = '1';
  if (import.meta.env.DEV) {
    console.warn('Javascript is running in DEV mode');
  }
  Header();
  activeSection();
  initCartUi();

  const inits = [
    import('../html/parts/Btn/Btn.js').then((m) => m.default()),
  ];

  // Skeleton zdjęć oferty / listingu — lekki, od razu (LCP galerii).
  if (has('.hski-img-skeleton, img.js-hski-img')) {
    inits.push(import('./imageSkeleton.js').then((m) => m.default()));
  }

  // Poniżej folda / czysto interakcyjne — leniwie, przy zbliżaniu się do viewportu.
  whenNear('.faq-item', () => import('./faqAccordion.js').then((m) => m.default()));
  // Listy „pokaż N + Pokaż szczegóły” (pakiety szkoły i inne miejsca z .js-expandable-list).
  whenNear('.js-expandable-list', () => import('./expandableList.js').then((m) => m.default()));
  // PhotoSwipe: home / oferta / produkt szkoły / Klub Pingwina (karty listingu — OfferZagraniczna + openGalleryFromLink).
  whenNear(
    '.home-gallery .gallery-js, .offer-page__gallery.gallery-js, .school-product-page__gallery-grid.gallery-js, .klub-pingwina__gallery-grid.gallery-js, .js-gallery-open-all',
    () => import('./Lightbox.js').then((m) => m.default()),
    '200px 0px',
  );
  whenNear('.js-school-infrastructure', () => import('../html/builder/sections/SchoolInfrastructure/SchoolInfrastructure.js').then((m) => m.default()));
  // Spinner listing → oferta: od razu (nie czekaj na Swiper / whenNear kart).
  if (has('.js-offers-list-zagraniczne, a.js-offer-cta, a.offer-zagraniczna__title-link')) {
    inits.push(import('./listingNavLoading.js').then((m) => m.bindOfferCardNavigationLoading()));
  }

  // Karty ofert: Swiper + pickery — po LCP (węższy rootMargin niż domyślne 400px).
  whenNear('.js-offer-gallery, .js-offer-term-picker, .js-offer-place-picker', () => {
    import('../html/parts/OfferZagraniczna/OfferZagraniczna.js').then((m) => m.default());
  }, '120px 0px');

  // Wyszukiwarka / listing AJAX — nie blokuj bootu (LCP karty / hero).
  whenNear('.js-offer-search', () => {
    import('../html/parts/OfferSearch/OfferSearch.js').then((m) => m.default());
  }, '80px 0px');
  if (has('.home-hero__video[data-src], .home-hero__video-embed-wrap[data-src]')) {
    inits.push(import('../html/builder/sections/Hero/HeroVideo.js').then((m) => m.default()));
  }
  // Home (Swiper) — nie konkuruj z LCP / OfferSearch; ładuj przy zbliżeniu do sliderów.
  whenNear('.js-hero-slider, .js-reviews-slider, .js-offers-tabs', () => {
    import('../html/pages/Home/Home.js').then((m) => m.default());
  });
  whenNear('.js-offers-list-zagraniczne', () => {
    import('../html/pages/OffersListZagraniczne/OffersListZagraniczne.js').then((m) => m.default());
  }, '80px 0px');
  whenNear('.js-offers-list-zagraniczne[data-unified="1"]', () => {
    import('../html/pages/OffersListZagraniczne/OffersUnifiedListing.js').then((m) => m.default());
  }, '80px 0px');
  // Single oferty: Offer.js od razu (przed SubNav) — zdejmij boot loader ASAP.
  if (has('.js-offer-page')) {
    inits.push(
      import('../html/pages/Offer/Offer.js')
        .then((m) => m.default())
        .catch(() => {
          window.__hskiClearOfferBoot?.();
        }),
    );
  }
  // Produkt szkoły (Pingwin / kursy / lekcje): od razu — boot loader + overlay przy swapie.
  if (has('.js-school-product')) {
    inits.push(
      import('../html/pages/SchoolProduct/SchoolProduct.js')
        .then((m) => m.default())
        .catch(() => {
          window.__hskiClearSchoolBoot?.();
        }),
    );
  }
  if (has('.js-klub-pingwina')) {
    inits.push(import('../html/pages/KlubPingwina/KlubPingwina.js').then((m) => m.default()));
  }
  if (has('.js-offer-sub-nav')) {
    inits.push(import('../html/parts/OfferSubNav/OfferSubNav.js').then((m) => m.default()));
  }
  if (has('.js-booking')) {
    inits.push(import('../html/pages/Booking/Booking.js').then((m) => m.default()));
  }
  if (has('.js-cart-page')) {
    inits.push(import('../html/pages/Cart/Cart.js').then((m) => m.default()));
  }
  if (has('.js-hski-video-play, .js-school-product-play, .js-review-video-play, .js-school-about-play, .js-school-about-poster, .js-hski-video-poster')) {
    inits.push(import('../html/builder/sections/SchoolAbout/SchoolAbout.js').then((m) => m.default()));
  }
  if (has('.js-school-courses')) {
    inits.push(import('../html/builder/sections/SchoolCourses/SchoolCourses.js').then((m) => m.default()));
  }
  // Oferta zagraniczna: szkolenie / program — dopiero przy zbliżeniu (nie na critical path).
  whenNear('.offer-training', () => {
    import('../html/builder/sections/SchoolCourses/SchoolCourses.js').then((m) => m.default());
  }, '200px 0px');
  whenNear('.js-offers-list-school', () => {
    import('../html/pages/OffersListSchool/OffersListSchool.js').then((m) => m.default());
  }, '80px 0px');
  // SchoolProduct.js ładowany eagerly wyżej (boot loader) — nie dubluj whenNear.
  if (has('.js-about-founders, .js-about-team-slider, .js-about-founders-story-modal, .js-about-team-modal')) {
    inits.push(import('../html/pages/About/About.js').then((m) => m.default()));
  }
  if (has('.js-blog-page')) {
    inits.push(import('../html/pages/Blog/Blog.js').then((m) => m.default()));
  }
  if (has('.js-article-page')) {
    inits.push(import('../html/pages/Article/Article.js').then((m) => m.default()));
  }
  if (has('.career-detail-page__field--file')) {
    inits.push(import('../html/pages/Career/CareerDetail.js').then((m) => m.default()));
  }

  await Promise.all(inits);
};

const bootApp = () => {
  if (document.querySelector('[include-html]')) {
    document.documentElement.addEventListener('includesLoaded', runApp, { once: true });
    return;
  }
  runApp();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootApp);
} else {
  bootApp();
}
